import { forwardRef, type ComponentPropsWithoutRef, type ElementType } from 'react';

import { cx } from '../internal/cx.js';
import { buildSpacingStyle, pickSpacingProps, type SpacingProps } from '../internal/spacing.js';

export type ContainerSize = '1' | '2' | '3' | '4';

export type ContainerProps = ComponentPropsWithoutRef<'div'> &
  SpacingProps & {
    /** Polymorphic element tag. */
    as?: ElementType;
    /** Max-width preset mapped to `--container-*` tokens. */
    size?: ContainerSize;
  };

/** Centered content container with max-width presets. Requires `@import '@nrx/ui/theme.css'`. */
export const Container = forwardRef<HTMLElement, ContainerProps>(function Container(
  {
    as,
    className,
    style,
    size = '4',
    p,
    px,
    py,
    pt,
    pr,
    pb,
    pl,
    m,
    mx,
    my,
    mt,
    mr,
    mb,
    ml,
    ...rest
  },
  ref,
): React.JSX.Element {
  const Component = as ?? 'div';
  const spacingStyle = buildSpacingStyle(
    pickSpacingProps({ p, px, py, pt, pr, pb, pl, m, mx, my, mt, mr, mb, ml }),
  );

  return (
    <Component
      ref={ref as never}
      className={cx('nrx-container', `nrx-container--${size}`, className)}
      style={{ ...spacingStyle, ...style }}
      {...rest}
    />
  );
});
